import React, { createContext, useContext, useEffect, useState } from 'react';
import { ProfileItem } from '../services/api/types';
import { useAuthentication, useApi, useLocalStorage } from './index';

import { STORAGE_KEY_USER_EXTENSION } from '../constants';

export interface UserContextProps {
  userExtension: string;
  setUserExtension: React.Dispatch<React.SetStateAction<string>>;
  userProfile: ProfileItem | null;
  setUserProfile: React.Dispatch<React.SetStateAction<ProfileItem | null>>;
  saveExtension: (extension: string) => void;
}

export const UserContext = createContext<UserContextProps | null>(null);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { api } = useApi();
  const { auth } = useAuthentication();
  const { withLocalStorage } = useLocalStorage();
  const [loading, setLoading] = useState(true);
  const [userExtension, setUserExtension] = useState<string>('');
  const [userProfile, setUserProfile] = useState<ProfileItem | null>(null);

  useEffect(() => {
    const extension = withLocalStorage(STORAGE_KEY_USER_EXTENSION);
    setUserExtension(extension ? extension : '');
  }, [withLocalStorage]);

  const saveExtension = (extension: string) => {
    setUserExtension(extension);
    withLocalStorage(STORAGE_KEY_USER_EXTENSION, extension);
  };

  const isUserSignedIn = auth.isUserSignedIn();

  useEffect(() => {
    let isMounted = true;
    const addProfileFromApi = async () => {
      const userEmail = auth.getUserEmail();
      const profileFromApi: ProfileItem = await api.get_profilesdata(userEmail);
      if (isMounted) {
        setUserProfile(profileFromApi);
        setLoading(false);
      }
    };
    if (!isUserSignedIn) {
      setLoading(false);
    } else {
      if (!userProfile) {
        addProfileFromApi();
      } else {
        setLoading(false);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [api, userProfile, auth, isUserSignedIn]);

  if (loading) {
    return null;
  }

  return (
    <UserContext.Provider
      value={{
        userExtension,
        setUserExtension,
        userProfile,
        setUserProfile,
        saveExtension,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export function useUser(): UserContextProps {
  const context = useContext(UserContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useUser outside of UserProvider');
}
